import type { ComponentPropsWithRef, ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "../../lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "inverse" | "outline";
type Size = "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60 disabled:pointer-events-none " +
  "min-h-11"; // ≥44px touch target (WCAG 2.5.8 / target size)

const variants: Record<Variant, string> = {
  // The amber "spark" lives on primary CTAs. Black text on Volt Amber clears AA
  // easily in both themes.
  primary:
    "bg-accent text-black hover:brightness-95 active:brightness-90 shadow-sm shadow-black/10",
  secondary:
    "border border-line bg-surface text-ink hover:border-accent hover:text-accent-strong",
  ghost: "text-ink hover:bg-surface-2",
  // High-contrast solid for placing on dark/brand backgrounds. brand-900 is a
  // fixed near-black in both themes, so text stays legible on the white fill.
  inverse: "bg-white text-brand-900 hover:bg-white/90 active:bg-white/80 shadow-sm",
  // Bordered translucent control for the always-dark hero stage. White text +
  // white hairline read clearly on the near-black hero in both site themes.
  outline: "border border-white/40 text-white hover:bg-white/10 hover:border-white/70",
};

const sizes: Record<Size, string> = {
  md: "px-4 text-sm",
  lg: "px-6 text-base",
};

function classes(variant: Variant, size: Size, className?: string) {
  return cn(base, variants[variant], sizes[size], className);
}

type CommonProps = { variant?: Variant; size?: Size; children: ReactNode; className?: string };

/** Internal navigation button (react-router Link). */
export function ButtonLink({
  to,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: CommonProps & { to: string } & Omit<ComponentPropsWithRef<typeof Link>, "to" | "className">) {
  return (
    <Link to={to} className={classes(variant, size, className)} {...rest}>
      {children}
    </Link>
  );
}

/** Anchor button for external / tel: / mailto: links (not react-router routes). */
export function ButtonAnchor({
  href,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: CommonProps & { href: string } & Omit<ComponentPropsWithRef<"a">, "href" | "className">) {
  return (
    <a href={href} className={classes(variant, size, className)} {...rest}>
      {children}
    </a>
  );
}

/** Native button (form submit, onClick actions). */
export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  type = "button",
  ...rest
}: CommonProps & ComponentPropsWithRef<"button">) {
  return (
    <button type={type} className={classes(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}
